import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { IAddress } from "../../../models/address";
import { HttpError } from "../../../utils/ajax";
import { IReduxState } from "../reducers.interfaces";
import { getBillingAddress } from "../utils";

/**
 * Arguments for saveBillingAddress thunk.
 */
interface SaveBillingAddressArgs {
    bypassAddressValidation?: boolean;
}

/**
 * Save the billing address for checkout.
 *
 * This thunk saves the billing address to the checkout session for
 * payment processing. Supports bypassing address validation for
 * cases where the user confirms their non-standard address.
 *
 * @param {boolean} [args.bypassAddressValidation=false] - Skip address validation for non-standard addresses
 * @returns {Promise<IAddress>} Promise resolving to the saved address (potentially normalized by SureAddress)
 *
 * @example
 * // Save billing address with validation
 * dispatch(saveBillingAddress({}));
 *
 * @example
 * // Save non-standard address bypassing validation
 * dispatch(saveBillingAddress({ bypassAddressValidation: true }));
 *
 * @throws {Error} If address validation fails and bypass not set
 *
 * @sideEffects
 * - Reads billing address from `state.form.billing_*` fields
 * - Makes API call to save address via checkoutAPI.saveBillingAddress
 * - May normalize address fields via SureAddress validation
 */
export const saveBillingAddress = createAsyncThunk<
    IAddress,
    SaveBillingAddressArgs,
    { rejectValue: { response: { body: unknown; status: number } } }
>(
    "checkout/saveBillingAddress",
    async (args, { getState, rejectWithValue }) => {
        const state = getState() as { checkout: IReduxState };
        const billingAddr = getBillingAddress(state.checkout);
        try {
            return await checkoutAPI.saveBillingAddress(
                billingAddr,
                args.bypassAddressValidation,
            );
        } catch (err) {
            if (err instanceof HttpError) {
                return rejectWithValue({
                    response: { body: err.body, status: err.status },
                });
            }
            throw err;
        }
    },
);
